import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getResponses, getTemplates, generateResponseLink } from "../../utils/api/endpoints/questionnaire";
import usePageTitle from "../../utils/hooks/usePageTitle";
import axios from "../../utils/api/axios";
import { ScopingFormBuilder } from "../../components/sales/ScopingFormBuilder";
import { useScopingSchemaEditor } from "../../components/sales/useScopingSchemaEditor";
import DevAutofill from "./DevAutofill";
import {
  ChevronDown,
  Plus,
  X,
  Sparkles,
  Building,
  Copy,
  FolderOpen,
  LayoutTemplate,
  ExternalLink,
  Mail,
  FileText,
  Settings,
  Check,
  TrendingUp,
  Flame,
  AlertTriangle,
  ArrowRight,
  Target,
  Clock,
  Activity,
  Wrench,
  Cpu,
  Shield,
} from "lucide-react";
import {
  SalesPageShell,
  SalesPageHeader,
  SalesPanel,
  SalesBadge,
  SalesLoading,
  SalesButton,
  SalesLinkButton,
  salesTokens,
} from "../../components/sales/sales-ui";

// ── Static lookups ─────────────────────────────────────────────────────────

const METHODOLOGY_SHORT: Record<string, string> = {
  WEB_APP:            "Web App",
  API:                "API",
  MOBILE_APP:         "Mobile",
  AI_LLM:             "AI / LLM",
  CODE_REVIEW:        "Code Review",
  EXTERNAL_NETWORK:   "Ext. Network",
  INTERNAL_NETWORK:   "Int. AD",
  VPN_FIREWALL_REVIEW:"VPN / Firewall",
  CLOUD_CONFIG:       "Cloud Config",
  CLOUD_PENTEST:      "Cloud Pentest",
  M365_REVIEW:        "M365",
};

const METHODOLOGY_ICON: Record<string, string> = {
  WEB_APP:            "🌍",
  API:                "⚡",
  MOBILE_APP:         "📱",
  AI_LLM:             "🤖",
  CODE_REVIEW:        "📂",
  EXTERNAL_NETWORK:   "🛜",
  INTERNAL_NETWORK:   "🖥",
  VPN_FIREWALL_REVIEW:"🔐",
  CLOUD_CONFIG:       "☁️",
  CLOUD_PENTEST:      "☁️",
  M365_REVIEW:        "📧",
};

// ── Utility helpers ────────────────────────────────────────────────────────

const getTempConfig = (confidence: string | undefined) => {
  if (confidence === "high")   return { label: "Hot",      dot: "bg-red-500",    bar: "bg-red-400",    badge: "bg-red-50 text-red-700 border-red-200" };
  if (confidence === "medium") return { label: "Warm",     dot: "bg-amber-400",  bar: "bg-amber-400",  badge: "bg-amber-50 text-amber-700 border-amber-200" };
  if (confidence === "low")    return { label: "Cold",     dot: "bg-blue-400",   bar: "bg-blue-400",   badge: "bg-blue-50 text-blue-700 border-blue-200" };
  return                              { label: "Unscored", dot: "bg-slate-300",  bar: "bg-slate-200",  badge: "bg-slate-50 text-slate-500 border-slate-200" };
};

const getTierBadge = (tier: string | undefined): string => {
  if (tier === "Startup")    return "bg-indigo-50 text-indigo-700 border-indigo-200";
  if (tier === "Growing")    return "bg-violet-50 text-violet-700 border-violet-200";
  if (tier === "Enterprise") return "bg-orange-50 text-orange-700 border-orange-200";
  return "bg-slate-50 text-slate-500 border-slate-200";
};

const relativeDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "—";
  const diffDays = (Date.now() - new Date(dateStr).getTime()) / 86_400_000;
  if (diffDays < 1)  return "Today";
  if (diffDays < 2)  return "Yesterday";
  if (diffDays < 7)  return `${Math.floor(diffDays)}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

const formatK = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
};

// ── DEV_AUTOFILL_SAMPLES extracted to DevAutofill.tsx ──

// ── Component ──────────────────────────────────────────────────────────────

const SalesDashboard: React.FC = () => {
  usePageTitle("Leads & Scoping Dashboard");
  const navigate = useNavigate();
  const isDev = process.env.NODE_ENV === "development" || process.env.REACT_APP_SHOW_DEV_TOOLS === "true";

  const [loading, setLoading]           = useState(true);
  const [responses, setResponses]       = useState<any[]>([]);
  const [templates, setTemplates]       = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen]   = useState(false);

  // Modal form states
  const [clientName, setClientName]             = useState("");
  const [clientEmail, setClientEmail]           = useState("");
  const [internalNote, setInternalNote]         = useState("");
  const [expiresDays, setExpiresDays]           = useState(30);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | "">("");
  const [isCustomizing, setIsCustomizing]       = useState(false);
  const [activeTab, setActiveTab]               = useState<"basics" | "builder">("basics");
  const [copied, setCopied]                     = useState(false);
  const [requireNda, setRequireNda]             = useState(false); // ── NDA toggle
  const schemaEditor = useScopingSchemaEditor(null);

  // Repositionable developer panel states removed (extracted to DevAutofill.tsx) ──
  const [businessUsers, setBusinessUsers]       = useState<any[]>([]);
  const [generatedLink, setGeneratedLink]       = useState<string | null>(null);
  const [selectedUser, setSelectedUser]         = useState<any | null>(null);
  const [togglingSow, setTogglingSow]           = useState(false);

  // ── Data loading ──────────────────────────────────────────────────────

  const loadData = async () => {
    try {
      setLoading(true);
      const [respList, tempList] = await Promise.all([getResponses(), getTemplates()]);
      setResponses(respList);
      setTemplates(tempList);
      if (tempList.length > 0) {
        setSelectedTemplateId(tempList[0].id);
        try { schemaEditor.resetEditor(JSON.parse(tempList[0].schema), 0); } catch { /* keep */ }
      }
      try {
        const usersRes = await axios.get("/v2/user/all?limit=250&roles=2");
        setBusinessUsers(usersRes.data?.users || []);
      } catch (err) {
        console.error("Failed to load business owners:", err);
      }
    } catch (err) {
      console.error("Failed to load sales scoping data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // ── Modal handlers ────────────────────────────────────────────────────

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    setSelectedTemplateId(id);
    const selected = templates.find(t => t.id === id);
    if (selected) {
      try { schemaEditor.resetEditor(JSON.parse(selected.schema), 0); } catch { /* ignore */ }
    }
  };

  const [showConfirmMintModal, setShowConfirmMintModal] = useState(false);

  const handleSubmitAttempt = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUser?.sow_exempt) {
      toast.error("Cannot generate scoping link: This client is SOW Intake Exempt.");
      return;
    }
    if (isCustomizing) {
      const errs = schemaEditor.validate();
      if (errs.length) {
        toast.error("Fix schema issues in the builder before generating");
        setActiveTab("builder");
        return;
      }
    }
    setShowConfirmMintModal(true);
  };

  const handleConfirmMint = async () => {
    setShowConfirmMintModal(false);
    const loader = toast.loading("Generating scoping link…");
    try {
      const res = await generateResponseLink({
        templateId: selectedTemplateId || undefined,
        clientName,
        clientEmail,
        internalNote,
        expiresDays,
        customSchemaSnapshot: isCustomizing ? schemaEditor.schema : undefined,
        requireNda, // ── NDA flag
      });
      setGeneratedLink(res.shareUrl);
      toast.success("Scoping link ready", { id: loader });
      loadData();
    } catch (err: any) {
      console.error("Failed to generate scoping session:", err);
      const detailedError = err.response?.data?.message || err.message || "Failed to generate scoping link";
      toast.error(detailedError, { id: loader });
    }
  };


  const handleToggleSOWExempt = async () => {
    if (!selectedUser) return;
    const newExemptValue = !selectedUser.sow_exempt;
    setTogglingSow(true);
    const loader = toast.loading(newExemptValue ? "Disabling SOW intake..." : "Enabling SOW intake...");
    try {
      await axios.post("/v2/user/status", {
        id: selectedUser.user_id,
        sow_exempt: newExemptValue,
      });

      // Update local states
      const updatedUser = { ...selectedUser, sow_exempt: newExemptValue };
      setSelectedUser(updatedUser);

      setBusinessUsers(prev => prev.map(u => u.user_id === selectedUser.user_id ? updatedUser : u));

      toast.success(newExemptValue ? "SOW Intake disabled (Exempt)" : "SOW Intake enabled (Required)", { id: loader });
    } catch (err: any) {
      console.error("Failed to toggle SOW status:", err);
      const detailedError = err.response?.data?.message || err.message || "Failed to update SOW status";
      toast.error(detailedError, { id: loader });
    } finally {
      setTogglingSow(false);
    }
  };

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyResponseLink = (shareUrl: string) => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard");
  };

  const resetModalForm = () => {
    setClientName(""); setClientEmail(""); setInternalNote("");
    setExpiresDays(30); setGeneratedLink(null);
    setIsCustomizing(false); setActiveTab("basics"); setCopied(false);
    setRequireNda(false); // ── Reset NDA toggle
    setSelectedUser(null);
    if (templates.length > 0) {
      setSelectedTemplateId(templates[0].id);
      try { schemaEditor.resetEditor(JSON.parse(templates[0].schema), 0); } catch { /* ignore */ }
    }
    setIsModalOpen(false);
  };

  // ── Analytics (all client-side from responses[]) ──────────────────────

  const analytics = useMemo(() => {
    const now = Date.now();

    const draftSessions     = responses.filter(r => r.status === "DRAFT");
    const submittedSessions = responses.filter(r => r.status === "SUBMITTED");
    const convertedSessions = responses.filter(r => r.status === "CONVERTED");
    const lostSessions      = responses.filter(r => r.status === "LOST");
    const rejectedSessions  = responses.filter(r => r.status === "REJECTED");
    const draftCount     = draftSessions.length;
    const submittedCount = submittedSessions.length;
    const convertedCount = convertedSessions.length;
    const lostCount      = lostSessions.length;
    const rejectedCount  = rejectedSessions.length;
    const total          = responses.length;

    // Conversion rate: converted / (submitted + converted + lost + rejected)
    const conversionRate = (submittedCount + convertedCount + lostCount + rejectedCount) > 0
      ? Math.round((convertedCount / (submittedCount + convertedCount + lostCount + rejectedCount)) * 100)
      : 0;

    // % of all sessions that have been submitted (or converted)
    const submitRate = total > 0
      ? Math.round(((submittedCount + convertedCount) / total) * 100)
      : 0;

    // Pipeline value: open = draft + submitted
    const pipelineValue = [...draftSessions, ...submittedSessions]
      .reduce((s, r) => s + (r.enrichment?.pricing?.standard?.adjustedPrice || 0), 0);

    // Won revenue: all converted
    const wonRevenue = convertedSessions
      .reduce((s, r) => s + (r.enrichment?.pricing?.standard?.adjustedPrice || 0), 0);

    // Avg quote across all quoted sessions
    const quotedSessions = responses.filter(r => (r.enrichment?.pricing?.standard?.adjustedPrice || 0) > 0);
    const avgQuote = quotedSessions.length > 0
      ? Math.round(quotedSessions.reduce((s, r) => s + (r.enrichment.pricing.standard.adjustedPrice || 0), 0) / quotedSessions.length)
      : 0;

    // Avg total engagement days (testing + reporting)
    const sessionsWithDays = responses.filter(r => (r.enrichment?.pricing?.standard?.testingDays || 0) > 0);
    const avgTotalDays = sessionsWithDays.length > 0
      ? Math.round(sessionsWithDays.reduce((s, r) => {
          const td = r.enrichment?.pricing?.standard?.testingDays || 0;
          const rd = r.enrichment?.pricing?.standard?.reportingDays || 0;
          return s + td + rd;
        }, 0) / sessionsWithDays.length)
      : 0;

    // Hot leads: high confidence + not yet converted
    const hotLeads = responses.filter(r =>
      r.enrichment?.internalSummary?.tierConfidence === "high" && r.status !== "CONVERTED"
    ).length;

    // Stale drafts: DRAFT older than 7 days
    const staleDrafts = draftSessions.filter(r => {
      if (!r.created_at) return false;
      return (now - new Date(r.created_at).getTime()) / 86_400_000 > 7;
    }).length;

    // Lead temperature breakdown
    const hotCount      = responses.filter(r => r.enrichment?.internalSummary?.tierConfidence === "high").length;
    const warmCount     = responses.filter(r => r.enrichment?.internalSummary?.tierConfidence === "medium").length;
    const coldCount     = responses.filter(r => r.enrichment?.internalSummary?.tierConfidence === "low").length;
    const noTempCount   = total - hotCount - warmCount - coldCount;

    // Methodology mix — primary: raw answers (assessment_type); fallback: classifiedAssets
    const methodCounts: Record<string, number> = {};
    responses.forEach(r => {
      // Primary: walk the actual intake answer tree (parsing if stored as a string)
      let parsedAnswers = r.answers;
      if (typeof parsedAnswers === "string") {
        try {
          parsedAnswers = JSON.parse(parsedAnswers);
        } catch (e) {
          parsedAnswers = {};
        }
      }
      const rawAssets: any[] = parsedAnswers?.business_assets || [];
      if (rawAssets.length > 0) {
        rawAssets.forEach((asset: any) => {
          (asset.assessment_scopes || []).forEach((scope: any) => {
            const m = scope.assessment_type;
            if (m) methodCounts[m] = (methodCounts[m] || 0) + 1;
          });
        });
      } else {
        // Fallback: classifiedAssets from enrichment (field may be undefined on older data)
        (r.enrichment?.pricing?.standard?.classifiedAssets || []).forEach((a: any) => {
          const m = a.methodology || a.assessment_type;
          if (m) methodCounts[m] = (methodCounts[m] || 0) + 1;
        });
      }
    });
    const sortedMethods  = Object.entries(methodCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);
    const maxMethodCount = sortedMethods[0]?.[1] || 1;

    // Tier distribution
    const tierCounts: Record<string, number> = {};
    responses.forEach(r => {
      const tier = r.enrichment?.internalSummary?.tier;
      if (tier) tierCounts[tier] = (tierCounts[tier] || 0) + 1;
    });
    const totalTiered = Object.values(tierCounts).reduce((s, v) => s + v, 0) || 1;

    // Activity velocity: avg days from created → submitted
    const sessionsWithBoth = responses.filter(r => r.submitted_at && r.created_at);
    const avgDaysToSubmit = sessionsWithBoth.length > 0
      ? sessionsWithBoth.reduce((s, r) =>
          s + (new Date(r.submitted_at).getTime() - new Date(r.created_at).getTime()), 0
        ) / sessionsWithBoth.length / 86_400_000
      : 0;

    // Recent activity: submitted in last 7 days
    const recentActivity = responses.filter(r => {
      if (!r.submitted_at) return false;
      return (now - new Date(r.submitted_at).getTime()) / 86_400_000 <= 7;
    }).length;

    // Primary currency (first enriched session)
    const currency = responses.find(r => r.enrichment?.pricing?.standard?.currency)
      ?.enrichment?.pricing?.standard?.currency || "NZD";

    return {
      draftCount, submittedCount, convertedCount, lostCount, rejectedCount, total,
      conversionRate, submitRate, pipelineValue, wonRevenue,
      avgQuote, avgTotalDays, hotLeads, staleDrafts,
      hotCount, warmCount, coldCount, noTempCount,
      sortedMethods, maxMethodCount,
      tierCounts, totalTiered,
      avgDaysToSubmit, recentActivity,
      currency,
    };
  }, [responses]);

  // ── Loading state ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <SalesPageShell>
        <SalesLoading message="Syncing scoping dashboard…" />
      </SalesPageShell>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <SalesPageShell>

      {/* ── Page header ── */}
      <SalesPageHeader
        eyebrow="Sales & engagement"
        title="Leads & scoping"
        description="Pipeline analytics, lead intelligence, and intake management."
        actions={
          <>
            <SalesLinkButton to="/dashboard/sales/templates" variant="secondary">
              <LayoutTemplate size={14} />
              Templates
            </SalesLinkButton>
            <SalesButton onClick={() => setIsModalOpen(true)} size="md">
              <Plus size={14} />
              New scoping link
            </SalesButton>
          </>
        }
      />

      {/* ── KPI Strip — 8 tiles ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">

        {/* Total Sessions */}
        <div className="relative bg-white rounded-xl border border-slate-200 p-4 overflow-hidden hover:shadow-sm hover:border-slate-300 transition group">
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-slate-400 group-hover:bg-ctb-blue transition-colors" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Total Sessions</span>
            <Activity size={13} className="text-slate-300" />
          </div>
          <div className="text-2xl font-black text-slate-800 tabular-nums pl-1">{analytics.total}</div>
          <div className="text-[9px] text-slate-400 pl-1 mt-0.5">all time</div>
        </div>

        {/* Conversion Rate */}
        <div className="relative bg-white rounded-xl border border-slate-200 p-4 overflow-hidden hover:shadow-sm hover:border-emerald-200 transition group">
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-emerald-500" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Conversion Rate</span>
            <TrendingUp size={13} className="text-emerald-300" />
          </div>
          <div className="text-2xl font-black text-emerald-600 tabular-nums pl-1">{analytics.conversionRate}%</div>
          <div className="text-[9px] text-slate-400 pl-1 mt-0.5">submit → convert</div>
        </div>

        {/* Open Pipeline */}
        <div className="relative bg-white rounded-xl border border-slate-200 p-4 overflow-hidden hover:shadow-sm hover:border-ctb-blue/30 transition group">
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-ctb-blue" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Open Pipeline</span>
            <Target size={13} className="text-ctb-blue/30" />
          </div>
          <div className="text-2xl font-black text-ctb-blue tabular-nums pl-1">
            {analytics.currency} {formatK(analytics.pipelineValue)}
          </div>
          <div className="text-[9px] text-slate-400 pl-1 mt-0.5">draft + submitted</div>
        </div>

        {/* Won Revenue */}
        <div className="relative bg-white rounded-xl border border-slate-200 p-4 overflow-hidden hover:shadow-sm hover:border-emerald-200 transition group">
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-emerald-600" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Won Revenue</span>
            <Check size={13} className="text-emerald-300" />
          </div>
          <div className="text-2xl font-black text-emerald-700 tabular-nums pl-1">
            {analytics.currency} {formatK(analytics.wonRevenue)}
          </div>
          <div className="text-[9px] text-slate-400 pl-1 mt-0.5">converted sessions</div>
        </div>

        {/* Avg Quote */}
        <div className="relative bg-white rounded-xl border border-slate-200 p-4 overflow-hidden hover:shadow-sm transition group">
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-violet-500" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Avg Quote</span>
            <span className="text-[9px] font-bold text-violet-300">{analytics.currency}</span>
          </div>
          <div className="text-2xl font-black text-violet-700 tabular-nums pl-1">
            {analytics.avgQuote > 0 ? formatK(analytics.avgQuote) : "—"}
          </div>
          <div className="text-[9px] text-slate-400 pl-1 mt-0.5">avg across quoted</div>
        </div>

        {/* Avg Scope Days */}
        <div className="relative bg-white rounded-xl border border-slate-200 p-4 overflow-hidden hover:shadow-sm transition group">
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-indigo-400" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Avg Scope Days</span>
            <Clock size={13} className="text-indigo-300" />
          </div>
          <div className="text-2xl font-black text-indigo-600 tabular-nums pl-1">
            {analytics.avgTotalDays > 0 ? analytics.avgTotalDays : "—"}
          </div>
          <div className="text-[9px] text-slate-400 pl-1 mt-0.5">testing + reporting</div>
        </div>

        {/* Hot Leads */}
        <div className="relative bg-white rounded-xl border border-red-100 p-4 overflow-hidden hover:shadow-sm hover:border-red-200 transition group">
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-red-500" />
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Hot Leads</span>
            <Flame size={13} className="text-red-300" />
          </div>
          <div className="text-2xl font-black text-red-600 tabular-nums pl-1">{analytics.hotLeads}</div>
          <div className="text-[9px] text-slate-400 pl-1 mt-0.5">high confidence, open</div>
        </div>

        {/* Stale Drafts */}
        <div className={`relative bg-white rounded-xl border p-4 overflow-hidden hover:shadow-sm transition group ${analytics.staleDrafts > 0 ? "border-amber-200" : "border-slate-200"}`}>
          <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${analytics.staleDrafts > 0 ? "bg-amber-500" : "bg-slate-300"}`} />
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Stale Drafts</span>
            <AlertTriangle size={13} className={analytics.staleDrafts > 0 ? "text-amber-400" : "text-slate-300"} />
          </div>
          <div className={`text-2xl font-black tabular-nums pl-1 ${analytics.staleDrafts > 0 ? "text-amber-600" : "text-slate-800"}`}>
            {analytics.staleDrafts}
          </div>
          <div className="text-[9px] text-slate-400 pl-1 mt-0.5">drafts &gt; 7 days old</div>
        </div>

      </div>

      {/* ── Pipeline Funnel + Lead Temperature ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">

        {/* Pipeline Funnel */}
        <SalesPanel title="Pipeline funnel" className="lg:col-span-3" bodyClassName="p-5">
          <div className="flex items-stretch gap-2">

            {/* DRAFT stage */}
            <div className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full rounded-xl bg-amber-50 border border-amber-200 p-4 text-center">
                <div className="text-[9px] font-bold uppercase tracking-wider text-amber-600 mb-1">Draft</div>
                <div className="text-3xl font-black text-amber-700 tabular-nums">{analytics.draftCount}</div>
                <div className="text-[9px] text-amber-500 mt-1">awaiting client</div>
              </div>
              <div className="text-[9px] font-semibold text-slate-400 tabular-nums">
                {analytics.total > 0 ? Math.round((analytics.draftCount / analytics.total) * 100) : 0}% of total
              </div>
            </div>

            {/* Arrow draft → submitted */}
            <div className="flex flex-col items-center justify-center gap-1 px-1 shrink-0">
              <div className="text-[9px] font-black text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 tabular-nums">
                {analytics.submitRate}%
              </div>
              <ArrowRight size={16} className="text-slate-300" />
              <div className="text-[8px] text-slate-400 font-semibold">response rate</div>
            </div>

            {/* SUBMITTED stage */}
            <div className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full rounded-xl bg-ctb-blue/5 border border-ctb-blue/20 p-4 text-center">
                <div className="text-[9px] font-bold uppercase tracking-wider text-ctb-blue mb-1">Submitted</div>
                <div className="text-3xl font-black text-ctb-blue tabular-nums">{analytics.submittedCount}</div>
                <div className="text-[9px] text-ctb-blue/60 mt-1">awaiting review</div>
              </div>
              <div className="text-[9px] font-semibold text-slate-400 tabular-nums">
                {analytics.total > 0 ? Math.round((analytics.submittedCount / analytics.total) * 100) : 0}% of total
              </div>
            </div>

            {/* Arrow submitted → converted */}
            <div className="flex flex-col items-center justify-center gap-1 px-1 shrink-0">
              <div className="text-[9px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 tabular-nums">
                {analytics.conversionRate}%
              </div>
              <ArrowRight size={16} className="text-slate-300" />
              <div className="text-[8px] text-slate-400 font-semibold">close rate</div>
            </div>

            {/* OUTCOMES stage */}
            <div className="flex-1 flex flex-col justify-between gap-2">
              {/* CONVERTED outcome */}
              <div className="flex-1 flex flex-col items-center">
                <div className="w-full rounded-xl bg-emerald-50 border border-emerald-200 p-2 text-center flex-1 flex flex-col justify-center">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 mb-0.5">Converted (Won)</div>
                  <div className="text-lg font-black text-emerald-700 tabular-nums">{analytics.convertedCount}</div>
                  <div className="text-[8px] font-semibold text-emerald-600 mt-0.5 tabular-nums">
                    {analytics.total > 0 ? Math.round((analytics.convertedCount / analytics.total) * 100) : 0}% of total
                  </div>
                </div>
              </div>

              {/* REJECTED outcome */}
              <div className="flex-1 flex flex-col items-center my-1">
                <div className="w-full rounded-xl bg-slate-50 border border-slate-200 p-2 text-center flex-1 flex flex-col justify-center">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-slate-600 mb-0.5">Rejected</div>
                  <div className="text-lg font-black text-slate-700 tabular-nums">{analytics.rejectedCount}</div>
                  <div className="text-[8px] font-semibold text-slate-500 mt-0.5 tabular-nums">
                    {analytics.total > 0 ? Math.round((analytics.rejectedCount / analytics.total) * 100) : 0}% of total
                  </div>
                </div>
              </div>

              {/* LOST outcome */}
              <div className="flex-1 flex flex-col items-center">
                <div className="w-full rounded-xl bg-rose-50 border border-rose-200 p-2 text-center flex-1 flex flex-col justify-center">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-rose-700 mb-0.5">Lost</div>
                  <div className="text-lg font-black text-rose-700 tabular-nums">{analytics.lostCount}</div>
                  <div className="text-[8px] font-semibold text-rose-600 mt-0.5 tabular-nums">
                    {analytics.total > 0 ? Math.round((analytics.lostCount / analytics.total) * 100) : 0}% of total
                  </div>
                </div>
              </div>
            </div>

          </div>
        </SalesPanel>

        {/* Lead Temperature */}
        <SalesPanel title="Lead temperature" className="lg:col-span-2" bodyClassName="p-5">
          <div className="space-y-3">
            {(
              [
                { label: "Hot",      count: analytics.hotCount,    conf: "high"    },
                { label: "Warm",     count: analytics.warmCount,   conf: "medium"  },
                { label: "Cold",     count: analytics.coldCount,   conf: "low"     },
                { label: "Unscored", count: analytics.noTempCount, conf: undefined },
              ] as const
            ).map(({ label, count, conf }) => {
              const cfg = getTempConfig(conf);
              const pct = analytics.total > 0 ? (count / analytics.total) * 100 : 0;
              return (
                <div key={label} className="flex items-center gap-3">
                  <div className="flex items-center gap-2 w-20 shrink-0">
                    <div className={`w-2 h-2 rounded-full ${cfg.dot} shrink-0`} />
                    <span className="text-[11px] font-semibold text-slate-700">{label}</span>
                  </div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${cfg.bar} rounded-full transition-all duration-500`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-[11px] font-black text-slate-700 tabular-nums w-5 text-right">{count}</span>
                  <span className="text-[9px] text-slate-400 w-8 tabular-nums">{Math.round(pct)}%</span>
                </div>
              );
            })}
          </div>

          {/* Velocity mini-stats */}
          <div className="mt-5 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">Avg Days to Submit</div>
              <div className="text-xl font-black text-slate-700 tabular-nums">
                {analytics.avgDaysToSubmit > 0 ? analytics.avgDaysToSubmit.toFixed(1) : "—"}
              </div>
              <div className="text-[9px] text-slate-400 mt-0.5">created → submitted</div>
            </div>
            <div>
              <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1">Active (Last 7d)</div>
              <div className="text-xl font-black text-ctb-blue tabular-nums">{analytics.recentActivity}</div>
              <div className="text-[9px] text-slate-400 mt-0.5">submissions this week</div>
            </div>
          </div>
        </SalesPanel>

      </div>

      {/* ── Methodology Mix + Tier Split ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

        {/* Top Assessment Types */}
        <SalesPanel title="Top assessment types" bodyClassName="p-5">
          {analytics.sortedMethods.length > 0 ? (
            <div className="space-y-2.5">
              {analytics.sortedMethods.map(([method, count]: [string, number]) => {
                const pct    = (count / analytics.maxMethodCount) * 100;
                const label  = METHODOLOGY_SHORT[method] || method.replace(/_/g, " ");
                const icon   = METHODOLOGY_ICON[method] || "🔒";
                return (
                  <div key={method} className="flex items-center gap-3">
                    <span className="text-sm leading-none shrink-0">{icon}</span>
                    <span className="text-[11px] font-semibold text-slate-700 w-28 shrink-0 truncate">{label}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-ctb-blue/60 rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-[11px] font-black text-slate-600 tabular-nums w-5 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center space-y-1.5">
              <div className="text-2xl">📊</div>
              <p className="text-sm font-semibold text-slate-400">No methodology data yet</p>
              <p className="text-[11px] text-slate-400">Populates once clients submit the intake form.</p>
            </div>
          )}
        </SalesPanel>

        {/* Client Tier Distribution */}
        <SalesPanel title="Client tier distribution" bodyClassName="p-5">
          {Object.keys(analytics.tierCounts).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(analytics.tierCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([tier, count]) => {
                  const pct      = (count / analytics.totalTiered) * 100;
                  const badgeCls = getTierBadge(tier);
                  return (
                    <div key={tier} className="flex items-center gap-3">
                      <span className={`text-[9px] font-black px-2 py-0.5 rounded border ${badgeCls} w-20 text-center shrink-0`}>
                        {tier}
                      </span>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-ctb-blue/60 to-ctb-blue/25 rounded-full transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[11px] font-black text-slate-600 tabular-nums w-5 text-right">{count}</span>
                      <span className="text-[9px] text-slate-400 w-8 tabular-nums">{Math.round(pct)}%</span>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="py-8 text-center space-y-1.5">
              <div className="text-2xl">🏷️</div>
              <p className="text-sm font-semibold text-slate-400">No tier data yet</p>
              <p className="text-[11px] text-slate-400">Tier is inferred after clients submit the intake form.</p>
            </div>
          )}
        </SalesPanel>

      </div>

      {/* ── Enriched Leads Table ── */}
      <SalesPanel
        title="Active scoping opportunities"
        bodyClassName="p-0"
        action={
          <SalesLinkButton to="/dashboard/sales/intakes" variant="secondary" className="!py-1 !px-2.5 flex items-center gap-1">
            <span>View All Intakes</span>
            <ArrowRight size={11} className="transition group-hover:translate-x-0.5" />
          </SalesLinkButton>
        }
      >
        <div className={salesTokens.tableWrap}>
          <table className={salesTokens.table}>
            <thead className={salesTokens.thead}>
              <tr>
                <th className={salesTokens.th}>Client</th>
                <th className={salesTokens.th}>Tier</th>
                <th className={salesTokens.th}>Temp</th>
                <th className={salesTokens.th}>Status</th>
                <th className={salesTokens.th}>Scope Days</th>
                <th className={salesTokens.th}>Quote</th>
                <th className={salesTokens.th}>Submitted</th>
                <th className={`${salesTokens.th} text-right`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {responses.filter(r => r.status !== "LOST" && r.status !== "REJECTED").slice(0, 5).map((resp) => {
                const tier        = resp.enrichment?.internalSummary?.tier;
                const confidence  = resp.enrichment?.internalSummary?.tierConfidence;
                const tempCfg     = getTempConfig(confidence);
                const tierBadge   = getTierBadge(tier);
                const testingDays = (resp.enrichment?.pricing?.standard?.testingDays  || 0)
                                  + (resp.enrichment?.pricing?.standard?.reportingDays || 0);
                const estPrice    = resp.enrichment?.pricing?.standard?.adjustedPrice;
                const currency    = resp.enrichment?.pricing?.standard?.currency || "NZD";
                const isStale     = resp.status === "DRAFT" && resp.created_at
                  && (Date.now() - new Date(resp.created_at).getTime()) / 86_400_000 > 7;

                return (
                  <tr
                    key={resp.id}
                    className={`${salesTokens.tr} cursor-pointer`}
                    onClick={() => navigate(`/dashboard/sales/scoping/${resp.id}`)}
                  >
                    {/* Client */}
                    <td className={salesTokens.td}>
                      <div className="flex items-center gap-2">
                        {isStale && (
                          <span title="Stale draft — no activity in 7+ days">
                            <AlertTriangle size={11} className="text-amber-500 shrink-0" />
                          </span>
                        )}
                        <div>
                          <div className="font-semibold text-slate-800">{resp.client_name || "Awaiting setup"}</div>
                          <div className="text-[10px] text-slate-400 truncate max-w-[180px]">{resp.client_email || "—"}</div>
                        </div>
                      </div>
                    </td>

                    {/* Tier */}
                    <td className={salesTokens.td}>
                      {tier ? (
                        <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border ${tierBadge}`}>
                          {tier}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>

                    {/* Temperature */}
                    <td className={salesTokens.td}>
                      {confidence ? (
                        <div className="flex items-center gap-1.5">
                          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${tempCfg.dot}`} />
                          <span className="text-[10px] font-semibold text-slate-600">{tempCfg.label}</span>
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className={salesTokens.td}>
                      <SalesBadge status={resp.status} isExpired={resp.isExpired} />
                    </td>

                    {/* Scope Days */}
                    <td className={`${salesTokens.td} tabular-nums`}>
                      {testingDays > 0 ? (
                        <span className="text-[11px] font-bold text-indigo-600">{testingDays}d</span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>

                    {/* Quote */}
                    <td className={`${salesTokens.td} tabular-nums`}>
                      {estPrice ? (
                        <span className="font-semibold text-slate-700">{currency} ${estPrice.toLocaleString()}</span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>

                    {/* Submitted date */}
                    <td className={`${salesTokens.td} text-[10px] text-slate-500`}>
                      {relativeDate(resp.submitted_at)}
                    </td>

                    {/* Actions */}
                    <td className={`${salesTokens.td} text-right`}>
                      <div className="flex items-center justify-end gap-2">
                        {resp.status === "DRAFT" && (
                          <SalesButton
                            variant="secondary"
                            onClick={(e) => { e.stopPropagation(); handleCopyResponseLink(resp.shareUrl); }}
                            title="Copy intake link"
                          >
                            <Copy size={12} />
                          </SalesButton>
                        )}
                        <SalesButton
                          variant="secondary"
                          onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/sales/scoping/${resp.id}`); }}
                        >
                          <ExternalLink size={12} />
                          Open
                        </SalesButton>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {responses.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-400 text-[11px]">
                    No scoping sessions yet. Create a link to send to your prospect.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SalesPanel>

      {/* ── Generate Link Modal (unchanged) ── */}
      {isModalOpen && createPortal(
        <>
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="w-full max-w-5xl bg-white border border-slate-200/90 rounded-2xl shadow-xl text-slate-800 relative overflow-hidden flex flex-col max-h-[90vh]">

            {/* Modal Header */}
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-ctb-blue/5 flex items-center justify-center shrink-0 border border-ctb-blue/10">
                  <Sparkles size={15} className="text-ctb-blue" />
                </div>
                <div>
                  <span className="text-[9px] font-semibold text-ctb-blue uppercase tracking-wide mb-0.5 inline-block">
                    New intake session
                  </span>
                  <h2 className="text-sm font-bold tracking-tight text-slate-900 leading-tight">
                    Generate scoping link
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={resetModalForm}
                className="w-8 h-8 rounded-full bg-white hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 border border-slate-200/80 shadow-sm transition duration-150"
              >
                <X size={14} />
              </button>
            </div>

            {/* Modal Wizard Tabs */}
            {!generatedLink && (
              <div className="px-6 bg-slate-50/30 border-b border-slate-150 flex space-x-6">
                <button
                  type="button"
                  onClick={() => setActiveTab("basics")}
                  className={`py-3 text-xs font-black transition-all relative flex items-center gap-1.5 ${
                    activeTab === "basics" ? "text-[#081549]" : "text-slate-400 hover:text-slate-600"
                  }`}
                >
                  <FileText size={13} />
                  <span>1. Setup Details</span>
                  {activeTab === "basics" && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#081549] rounded-full" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setActiveTab("builder"); setIsCustomizing(true); }}
                  className={`py-3 text-xs font-black transition-all relative flex items-center space-x-1.5 ${
                    activeTab === "builder" ? "text-[#081549]" : "text-slate-400 hover:text-slate-600"
                  }`}
                >
                  <Settings size={13} />
                  <span>2. Intake Form Builder</span>
                  {isCustomizing ? (
                    <span className="bg-lime-500/20 text-[#081549] border border-lime-500/30 text-[8px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider scale-90">
                      Customized
                    </span>
                  ) : (
                    <span className="bg-slate-100 text-slate-400 text-[8px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider scale-90">
                      Template
                    </span>
                  )}
                  {activeTab === "builder" && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#081549] rounded-full" />
                  )}
                </button>
              </div>
            )}



            {/* Modal Body */}
            {generatedLink ? (
              <div className="p-8 space-y-6 text-center max-w-lg mx-auto animate-fadeIn">
                <div className="w-16 h-16 bg-lime-500/20 text-lime-600 text-2xl rounded-full flex items-center justify-center mx-auto border-2 border-lime-500/30 shadow-[0_0_15px_rgba(132,204,22,0.15)] animate-pulse">
                  <Check size={28} strokeWidth={3} className="text-lime-600" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-black text-slate-900">Scoping link generated!</h3>
                  <p className="text-xs text-slate-500 leading-relaxed font-medium">
                    Send this unique URL to your client. They can complete the form dynamically, and their progress will autosave in real-time.
                  </p>
                </div>

                {/* Generated link success info cards */}
                <div className="grid grid-cols-3 gap-2 text-left">
                  <div className="p-2.5 rounded-xl bg-emerald-50 border border-emerald-200 space-y-0.5">
                    <span className="block text-[8px] font-black uppercase tracking-wider text-emerald-700">🛡 Token Strength</span>
                    <span className="block text-xs font-black text-emerald-800">128-bit entropy</span>
                    <span className="block text-[8px] text-emerald-600 font-semibold">Brute-force proof</span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-amber-50 border border-amber-200 space-y-0.5">
                    <span className="block text-[8px] font-black uppercase tracking-wider text-amber-700">⏱ Expiry Window</span>
                    <span className="block text-xs font-black text-amber-800">{expiresDays}d from now</span>
                    <span className="block text-[8px] text-amber-600 font-semibold">Auto-invalidates</span>
                  </div>
                  <div className="p-2.5 rounded-xl bg-[#081549]/5 border border-[#081549]/10 space-y-0.5">
                    <span className="block text-[8px] font-black uppercase tracking-wider text-[#081549]">🔒 Submit Lock</span>
                    <span className="block text-xs font-black text-[#081549]">One-way write</span>
                    <span className="block text-[8px] text-[#081549]/70 font-semibold">Post-submit locked</span>
                  </div>
                </div>

                {/* NDA badge if enabled */}
                {requireNda && (
                  <div className="flex items-center gap-2.5 p-3 rounded-xl bg-[#081549]/5
                                  border border-[#081549]/15 text-[#081549]">
                    <Shield size={14} className="text-[#081549] shrink-0" />
                    <div className="text-left">
                      <span className="block text-[8px] font-black uppercase tracking-wider text-[#081549]/80">NDA Required</span>
                      <span className="block text-[10px] font-bold">Client must sign NDA before accessing the form</span>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2 bg-slate-50 p-2.5 rounded-2xl border border-slate-200/80">
                  <input
                    type="text"
                    readOnly
                    value={generatedLink}
                    className="flex-1 bg-transparent text-xs text-[#081549] focus:outline-none select-all font-mono font-bold px-2"
                  />
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all duration-200 flex items-center gap-1 shrink-0 ${
                      copied
                        ? "bg-lime-500 text-[#081549] shadow-sm shadow-lime-500/10"
                        : "bg-[#081549] hover:bg-[#081549]/90 text-white shadow-md active:scale-95"
                    }`}
                  >
                    {copied ? (
                      <><Check size={11} strokeWidth={3} /><span>Copied!</span></>
                    ) : (
                      <><Copy size={11} /><span>Copy Link</span></>
                    )}
                  </button>
                </div>

                <div className="pt-4 flex justify-center">
                  <button
                    onClick={resetModalForm}
                    className="px-6 py-2.5 bg-[#081549] hover:bg-lime-500 hover:text-[#081549] text-white border border-[#081549] rounded-xl text-xs font-black transition-all shadow-md active:scale-98"
                  >
                    Done &amp; Close
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmitAttempt} className="flex flex-col flex-1 overflow-hidden">
                <div className="p-6 overflow-y-auto flex-1 max-h-[58vh] pr-2 scrollbar-thin scrollbar-thumb-slate-200">


                  {activeTab === "basics" && (
                    <div className="space-y-6 animate-fadeIn">

                      {/* SOW Exemption Status and Toggle */}
                      {selectedUser && (
                        <div className="p-4 rounded-xl border bg-slate-50 border-slate-200/80 flex items-center justify-between animate-fadeIn mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
                              !selectedUser.sow_exempt 
                                ? "bg-emerald-50 text-emerald-600 border-emerald-200/50" 
                                : "bg-amber-50 text-amber-600 border-amber-200/50"
                            }`}>
                              {!selectedUser.sow_exempt ? (
                                <Check size={18} className="text-emerald-600 font-bold" />
                              ) : (
                                <X size={18} className="text-amber-600 font-bold" />
                              )}
                            </div>
                            <div className="text-left">
                              <span className="block text-[8px] font-black uppercase tracking-wider text-slate-400">
                                SOW Config for {selectedUser.display_name || selectedUser.username}
                              </span>
                              <span className="block text-xs font-bold text-slate-800">
                                SOW Status: {!selectedUser.sow_exempt ? (
                                  <span className="text-emerald-600 font-black">ENABLED (Required)</span>
                                ) : (
                                  <span className="text-amber-600 font-black">DISABLED (Exempt)</span>
                                )}
                              </span>
                              <span className="block text-[10px] text-slate-500 mt-0.5 leading-tight">
                                {!selectedUser.sow_exempt 
                                  ? "This client must submit scoping form before SOW generation."
                                  : "This client is exempt from completing the intake form."}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                              {!selectedUser.sow_exempt ? "Required" : "Exempt"}
                            </span>
                            <button
                              type="button"
                              onClick={handleToggleSOWExempt}
                              disabled={togglingSow}
                              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                !selectedUser.sow_exempt ? "bg-emerald-500" : "bg-slate-300"
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  !selectedUser.sow_exempt ? "translate-x-5" : "translate-x-0"
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Existing client pre-fill */}
                      <div className="space-y-1.5 text-left">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          Select Active SOW Business Client (Optional — Pre-fills fields)
                        </label>
                        <div className="relative">
                          <select
                            value={selectedUser?.email || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val) {
                                const selected = businessUsers.find(u => u.email === val);
                                if (selected) {
                                  setSelectedUser(selected);
                                  setClientEmail(selected.email);
                                  setClientName(selected.Company?.company_name || selected.display_name || selected.username || "");
                                }
                              } else {
                                setSelectedUser(null);
                                setClientEmail(""); setClientName("");
                              }
                            }}
                            className="w-full h-11 pl-10 pr-10 bg-slate-50/65 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 hover:border-slate-350 hover:bg-slate-50/90 transition duration-200 appearance-none"
                          >
                            <option value="">-- Choose Existing Business Owner (Optional) --</option>
                            {businessUsers.map((u: any) => {
                              const isRequired = u.sow_exempt === false || u.sow_exempt === 0 || u.sow_exempt === "false";
                              const companyLabel = u.Company?.company_name ? ` [${u.Company.company_name}]` : "";
                              return (
                                <option key={u.user_id} value={u.email}>
                                  {u.display_name || u.username}{companyLabel} ({u.email}) — {isRequired ? "SOW Intake Required" : "SOW Intake Exempt"}
                                </option>
                              );
                            })}
                          </select>
                          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                            <Building size={14} />
                          </div>
                          <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none text-slate-400">
                            <ChevronDown size={14} />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-left">
                        <div className="space-y-1.5">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            Client / Prospect Company Name
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              required
                              placeholder="e.g. Acme Fintech Corp"
                              value={clientName}
                              onChange={(e) => setClientName(e.target.value)}
                              className="w-full h-11 pl-10 pr-4 bg-slate-50/65 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 hover:border-slate-350 hover:bg-slate-50/90 transition duration-200"
                            />
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                              <Building size={14} />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            Prospect Email (For automatic login matching)
                          </label>
                          <div className="relative">
                            <input
                              type="email"
                              required
                              placeholder="e.g. security@acme.com"
                              value={clientEmail}
                              onChange={(e) => setClientEmail(e.target.value)}
                              className="w-full h-11 pl-10 pr-4 bg-slate-50/65 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 hover:border-slate-350 hover:bg-slate-50/90 transition duration-200"
                            />
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                              <Mail size={14} />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-left">
                        <div className="space-y-1.5">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            Select Base Template
                          </label>
                          <div className="relative">
                            <select
                              value={selectedTemplateId}
                              onChange={handleTemplateChange}
                              className="w-full h-11 pl-10 pr-10 bg-slate-50/65 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 hover:border-slate-350 hover:bg-slate-50/90 transition duration-200 appearance-none"
                            >
                              {templates.length > 0 ? (
                                templates.map(t => (
                                  <option key={t.id} value={t.id}>{t.name}</option>
                                ))
                              ) : (
                                <option value="" disabled>Standard Scope Template</option>
                              )}
                            </select>
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                              <FolderOpen size={14} />
                            </div>
                            <div className="absolute inset-y-0 right-0 pr-3.5 flex items-center pointer-events-none text-slate-400">
                              <ChevronDown size={14} />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                              Link Validity (Days)
                            </label>
                            <span className={`text-[10px] font-black px-2.5 py-0.5 rounded-full border ${
                              expiresDays <= 7
                                ? "bg-red-50 text-red-600 border-red-200/60"
                                : expiresDays <= 14
                                ? "bg-amber-50 text-amber-600 border-amber-200/60"
                                : "bg-emerald-50 text-emerald-600 border-emerald-200/60"
                            }`}>
                              {expiresDays} days
                            </span>
                          </div>
                          <div className="flex items-center h-11 px-1">
                            <input
                              type="range"
                              min={1} max={90} step={1}
                              value={expiresDays}
                              onChange={(e) => setExpiresDays(Number(e.target.value))}
                              className="w-full accent-[#081549] cursor-pointer h-1.5 rounded-lg appearance-none bg-slate-100 border border-slate-200/50"
                            />
                          </div>
                          <div className="flex justify-between text-[9px] font-bold text-slate-400 px-1">
                            <span>1d (Tight)</span>
                            <span>30d (Standard)</span>
                            <span>90d (Max)</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1.5 text-left">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                          Internal Sales Scoping Notes
                        </label>
                        <textarea
                          rows={3}
                          placeholder="Describe context, budget hearsay, competitor information..."
                          value={internalNote}
                          onChange={(e) => setInternalNote(e.target.value)}
                          className="w-full p-3.5 bg-slate-50/65 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 hover:border-slate-350 hover:bg-slate-50/90 transition duration-200 resize-none"
                        />
                      </div>

                      {/* NDA Require Signature Toggle */}
                      <div className={`p-4 rounded-xl border transition-all duration-200 ${requireNda ? "border-[#081549]/20 bg-[#081549]/5" : "border-slate-200 bg-slate-50/40"}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <Shield size={16} className={`shrink-0 transition-colors duration-200 ${requireNda ? "text-[#081549]" : "text-slate-400"}`} />
                            <div className="text-left">
                              <label className={`block text-xs font-bold transition-colors duration-200 ${requireNda ? "text-[#081549]" : "text-slate-700"}`}>
                                Require NDA Signature
                              </label>
                              <p className={`text-[10px] mt-0.5 leading-snug transition-colors duration-200 ${requireNda ? "text-[#081549]/80" : "text-slate-500"}`}>
                                {requireNda
                                  ? "Client must sign the NDA before accessing the scoping form."
                                  : "Client can access the form without signing an NDA."}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setRequireNda(v => !v)}
                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 transition-colors duration-200 ease-in-out focus:outline-none ${requireNda ? "bg-ctb-green-300 border-transparent" : "bg-slate-200 border-slate-400"}`}
                            role="switch"
                            aria-checked={requireNda}
                          >
                            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full shadow ring-0 transition duration-200 ease-in-out ${requireNda ? "bg-white translate-x-5" : "bg-ctb-green-300 translate-x-0"}`} />
                          </button>
                        </div>
                      </div>

                    </div>
                  )}

                  {activeTab === "builder" && schemaEditor.schema && isCustomizing && (
                    <ScopingFormBuilder
                      editor={schemaEditor}
                      templateName={clientName || "New client"}
                      showPreview
                      compact
                    />
                  )}
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-150 flex justify-between items-center rounded-b-3xl select-none">
                  <div>
                    {activeTab === "builder" ? (
                      <button
                        type="button"
                        onClick={() => setActiveTab("basics")}
                        className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl text-xs font-bold transition shadow-sm active:scale-98"
                      >
                        Back to details
                      </button>
                    ) : (
                      isCustomizing && (
                        <button
                          type="button"
                          onClick={() => setActiveTab("builder")}
                          className="px-4 py-2 bg-[#081549]/5 hover:bg-[#081549] hover:text-white text-[#081549] border border-[#081549]/15 rounded-xl text-xs font-black transition shadow-sm active:scale-98"
                        >
                          Open intake builder
                        </button>
                      )
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={resetModalForm}
                      className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl text-xs font-bold transition shadow-sm active:scale-98"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={selectedUser?.sow_exempt}
                      className={`px-5 py-2 rounded-xl text-xs font-black transition-all duration-200 shadow-md border ${
                        selectedUser?.sow_exempt
                          ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed opacity-50 shadow-none"
                          : "bg-[#081549] border-[#081549] hover:bg-lime-500 hover:text-[#081549] hover:border-lime-500 text-white active:scale-98"
                      }`}
                    >
                      Generate scoping link
                    </button>
                  </div>
                </div>
              </form>
            )}

          </div>
        </div>

        {/* Detached & Ultra-compact collapsible developer autofill widget */}
        {isDev && (
          <DevAutofill
            templates={templates}
            selectedTemplateId={selectedTemplateId}
            setClientName={setClientName}
            setClientEmail={setClientEmail}
            setInternalNote={setInternalNote}
            setExpiresDays={setExpiresDays}
            setIsCustomizing={setIsCustomizing}
            setActiveTab={setActiveTab}
            schemaEditor={schemaEditor}
            generatedLink={generatedLink}
          />
        )}
      </>,
      document.body
    )}

    {/* ── Confirmation Modal ── */}
    {showConfirmMintModal && createPortal(
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200">
        <div className="w-full max-w-md bg-white border border-slate-200/80 rounded-2xl shadow-2xl p-6 text-slate-800 relative overflow-hidden flex flex-col animate-in zoom-in duration-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-ctb-blue/10 flex items-center justify-center shrink-0 border border-ctb-blue/20">
              <Sparkles size={20} className="text-ctb-blue" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 leading-tight">Generate Scoping Link</h3>
              <p className="text-[10px] text-slate-500 mt-0.5">Please confirm the intake session details</p>
            </div>
          </div>
          
          <div className="space-y-2.5 bg-slate-50 p-4 rounded-xl border border-slate-100 mb-6 text-xs text-slate-600">
            <div className="flex justify-between">
              <span className="font-medium text-slate-400 text-[10px] uppercase">Client:</span>
              <span className="font-bold text-slate-800">{clientName}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-slate-400 text-[10px] uppercase">Email:</span>
              <span className="font-semibold text-slate-700">{clientEmail}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-slate-400 text-[10px] uppercase">Expiry:</span>
              <span className="font-semibold text-slate-700">{expiresDays} Days</span>
            </div>
            {isCustomizing && (
              <div className="flex justify-between items-center pt-1.5 border-t border-slate-200/50">
                <span className="font-medium text-slate-400 text-[10px] uppercase">Questions:</span>
                <span className="bg-lime-500/20 text-[#081549] border border-lime-500/30 text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider scale-90">Custom Form Snapshot</span>
              </div>
            )}
          </div>

          <p className="text-xs text-slate-650 mb-6 leading-relaxed">
            Are you sure you want to generate a new secure intake session with these parameters? A unique link will be generated and marked active.
          </p>

          <div className="flex justify-end gap-2.5">
            <button
              type="button"
              onClick={() => setShowConfirmMintModal(false)}
              className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl text-xs font-semibold transition active:scale-98"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmMint}
              className="px-5 py-2 bg-[#081549] hover:bg-[#081549]/90 text-white rounded-xl text-xs font-bold transition-all shadow-md active:scale-98"
            >
              Confirm &amp; Generate
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}

    </SalesPageShell>
  );
};

export default SalesDashboard;

